///
/// EarthBrowser
///
/// Created by Matt Giger on September 23, 2026
/// Copyright (c) 2026 Geodata Labs
///
/// User insights API: site-wide sign-in/activity statistics and the
/// per-user detail view. Detail provides the UserAccounts tag so it
/// refetches after suspend/unsuspend/role mutations.
///

import { authenticatedApi } from './authenticatedApi'

export interface SigninDailyRow {
  day: string
  signins: number
  unique_users: number
  registrations: number
}

export interface SigninMethodRow {
  method: string
  provider: string | null
  count: number
}

export interface SigninStats {
  daily: SigninDailyRow[]
  methods: SigninMethodRow[]
  totals: {
    signins_today: number
    unique_7d: number
    unique_30d: number
  }
  activity: {
    active_today: number
    active_7d: number
    active_30d: number
    total_users: number
  }
}

export interface UserDetailProfile {
  id: number
  username: string | null
  email: string
  first_name: string | null
  last_name: string | null
  image_url: string | null
  status: string
  created_at: string
  email_verified: boolean
  email_verified_at: string | null
  last_login_at: string | null
  active_days: number
  last_active_day: string | null
  suspension_reason: string | null
  suspended_until: string | null
  deletion_requested_at: string | null
}

export interface UserDetail {
  user: UserDetailProfile
  roles: string[]
  subscriptions: {
    status: string
    current_period_end: string | null
    cancel_at_period_end: boolean
    created_at: string
    product_name: string
  }[]
  linkedAccounts: {
    method: string
    provider: string | null
    email: string | null
    is_verified: boolean
    created_at: string
  }[]
  passkeys: {
    name: string | null
    credential_device_type: string | null
    created_at: string
    last_used_at: string | null
  }[]
  devices: {
    id: number
    device_name: string
    device_type: string
    device_model: string
    os_name: string
    os_version: string
    status: string
    created_at: string
    last_used_at: string | null
    last_ip_address: string | null
  }[]
  activeSessions: number
  signins: {
    method: string
    provider: string | null
    kind: string
    ip_address: string | null
    created_at: string
  }[]
  content: {
    saved_views: number
    placemarks: number
    shares: number
    datasets: number
    placemark_alerts: number
    push_subscriptions: number
  }
  suppression: { reason: string; added_at: string } | null
}

export interface UserAuditEntry {
  id: number
  action: string
  resource_type: string | null
  resource_id: string | null
  metadata: unknown
  created_at: string
  user_id: number | null
  actor_id: number | null
  actor_email: string | null
}

export interface RetentionCohort {
  week: string
  cohort_size: number
  active_7d: number
  verified: number
  avg_active_days: number
}

export const userInsightsApi = authenticatedApi.injectEndpoints({
  endpoints: (builder) => ({
    getSigninStats: builder.query<SigninStats, void>({
      query: () => '/api/admin/signin-stats',
      providesTags: ['UserAccounts'],
    }),

    getUserDetail: builder.query<UserDetail, number>({
      query: (id) => `/api/admin/users/${id}/detail`,
      providesTags: ['UserAccounts'],
    }),

    getUserAudit: builder.query<{ entries: UserAuditEntry[] }, number>({
      query: (id) => `/api/admin/users/${id}/audit`,
      providesTags: ['UserAccounts'],
    }),

    getRetention: builder.query<{ cohorts: RetentionCohort[] }, void>({
      query: () => '/api/admin/retention',
      providesTags: ['UserAccounts'],
    }),

    revokeUserSessions: builder.mutation<{ success: boolean; revoked: number }, number>({
      query: (id) => ({
        url: `/api/admin/users/${id}/revoke-sessions`,
        method: 'POST',
      }),
      invalidatesTags: ['UserAccounts'],
    }),

    revokeUserDevice: builder.mutation<{ success: boolean }, { userId: number; deviceId: number }>({
      query: ({ userId, deviceId }) => ({
        url: `/api/admin/users/${userId}/devices/${deviceId}/revoke`,
        method: 'POST',
      }),
      invalidatesTags: ['UserAccounts'],
    }),
  }),
})

export const {
  useGetSigninStatsQuery,
  useGetUserDetailQuery,
  useGetUserAuditQuery,
  useGetRetentionQuery,
  useRevokeUserSessionsMutation,
  useRevokeUserDeviceMutation,
} = userInsightsApi
